import Cookies, { CookieGetOptions, CookieSetOptions } from 'universal-cookie'

import { STORAGE_KEYS_PREFIX } from '@/utils/constants'

type StorageEngines = 'cookies' | 'localStorage' | 'sessionStorage'

const prefixStorageKey = (key: string): string => (
  `${STORAGE_KEYS_PREFIX}${key}`
)

/**
 * Returns universal facade storage object.
 * @param engine
 */
const storageInstance = (engine: StorageEngines) => {
  const cookies = new Cookies()

  const getItem = <Value = unknown>(key: string, cookieOptions?: CookieGetOptions): Value | undefined => {
    const storageKey = prefixStorageKey(key)

    if (engine === 'cookies') {
      return cookies.get(storageKey, cookieOptions)
    }

    const value = window[engine].getItem(storageKey)

    return value ? JSON.parse(value) : undefined
  }

  const hasItem = (key: string, cookieOptions?: CookieGetOptions): boolean => {
    const storageKey = prefixStorageKey(key)

    if (engine === 'cookies') {
      return !!cookies.get(storageKey, cookieOptions)
    }

    return !!window[engine].getItem(storageKey)
  }

  const setItem = (key: string, value: unknown, cookieOptions?: CookieSetOptions): void => {
    const storageKey = prefixStorageKey(key)

    if (engine === 'cookies') {
      return cookies.set(storageKey, value, cookieOptions)
    }

    return window[engine].setItem(storageKey, JSON.stringify(value))
  }

  const removeItem = (key: string, cookieOptions: CookieSetOptions): void => {
    const storageKey = prefixStorageKey(key)

    if (engine === 'cookies') {
      return cookies.remove(storageKey, cookieOptions)
    }

    return window[engine].removeItem(storageKey)
  }

  return {
    getItem,
    hasItem,
    setItem,
    removeItem
  }
}

export default storageInstance
